import { useState, useEffect, useRef, useCallback } from "react";
import { Typography, Box, Stack } from "@mui/material";
import { keyframes } from "@mui/system";
import { styled } from "@mui/material/styles";

/**
 *   label       {string}   — Static label shown above
 *   items       {Array}    — Array of { value: string|number, suffix?: string, hidden?: boolean }
 *   interval    {number}   — Auto-slide interval in ms (default: 3000)
 *   className   {string}   — Optional extra class on the card wrapper
 */

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0);   }
`;


const Dot = styled("button")(({ theme, active }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  border: "none",
  padding: 0,
  cursor: "pointer",
  background: active ? "#0d3d30" : "#cbd5d1",
  transform: active ? "scale(1.25)" : "scale(1)",
  transition: "background 0.25s ease, transform 0.2s ease",
  [theme.breakpoints.up("xs")]: {
    width: 6,
    height: 6,
  },
  [theme.breakpoints.up("md")]: {
    width: 8,
    height: 8,
  },
  "&:hover": {
    background: active ? "#0d3d30" : "#92b0a8",
  },
}));

export default function ValueCarousel({
  label = "Value",
  items = [],
  interval = 3000,
  className = "",
}) {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  const itemsToShow = items?.filter((item) => !item.hidden);

  const goTo = useCallback((index) => {
    setCurrent(index);
  }, []);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % itemsToShow.length);
    }, interval);
  }, [interval, itemsToShow.length]);

  useEffect(() => {
    if (itemsToShow.length > 1) startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer, itemsToShow.length]);

  const handleDotClick = (index) => {
    goTo(index);
    startTimer(); // restart timer on manual click
  };

  if (!itemsToShow.length) return null;

  const activeItem = itemsToShow[current];

  return (
    <Stack width={"100%"} height={"100%"} alignItems={"center"} justifyContent={"center"}>
      <Typography
        color={"#4D6767"}
        fontWeight={400}
        fontSize={{ xs: "8px", md: "16px" }}>
        {label}
      </Typography>

      <Stack direction={"row"} gap={1} alignItems={"center"}>
        <Typography
          component={"span"}
          color={"#004135"}
          fontWeight={700}
          sx={{ animation: `${fadeIn} 0.35s ease` }}
          fontSize={{ xs: "12px", md: "20px" }}
          key={current}>
          {activeItem.value}
        </Typography>
        {activeItem.suffix && (
          <Typography
            component={"span"}
            fontWeight={400}
            color={"#004135"}
            fontSize={{ xs: "6px", sm: "8px", md: "10px" }}
            paddingX={"2px"}>
            {activeItem.suffix}
          </Typography>
        )}
      </Stack>

      {itemsToShow.length > 1 && (
        <Box
          display={"flex"}
          gap={1.5}
          my={1}
          alignItems={"center"}
          role="tablist"
          aria-label="Carousel navigation">
          {itemsToShow.map((_, i) => (
            <Dot
              key={i}
              onClick={() => handleDotClick(i)}
              active={i === current}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
}
